import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";

function formatDate(dateStr) {
  if (!dateStr || dateStr === "Coming Soon") return "Coming Soon";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch { return dateStr; }
}

const placeholderPosts = [
  { title: "Why Most EDR Deployments Fail in the First 90 Days", category: "EDR", excerpt: "Common pitfalls and how to avoid them in your next deployment.", readTime: "6 min read", date: "Coming Soon" },
  { title: "Threat Hunting with CrowdStrike: Where to Start", category: "Threat Hunting", excerpt: "A practical guide to getting started with proactive threat hunting.", readTime: "8 min read", date: "Coming Soon" },
  { title: "NG-SIEM vs Traditional SIEM: What Actually Changes", category: "SIEM", excerpt: "Breaking down the real operational differences for your SOC.", readTime: "7 min read", date: "Coming Soon" },
];

export default function Blog({ posts = [] }) {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Show real posts first, then fill remaining slots with placeholders
  const realPosts = posts.slice(0, 3);
  const remainingSlots = 3 - realPosts.length;
  const placeholders = remainingSlots > 0 ? placeholderPosts.slice(0, remainingSlots) : [];
  const hasPlaceholders = placeholders.length > 0;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await fetch(`${BACKEND_URL}/api/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setSubscribed(true);
      setEmail("");
    } catch {
      // silent fail
    }
  };

  return (
    <section id="blog" className="py-20 md:py-32" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#06B6D4]">Blog</span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold tracking-tight text-[#111827] mt-3">
            From the Field
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Real posts */}
          {realPosts.map((post, i) => (
            <div
              key={post.id || i}
              className="bg-white border border-[#E5E7EB] rounded-2xl p-6 card-hover cursor-pointer"
              data-testid={`blog-card-${i}`}
              onClick={() => post.id && navigate(`/blog/${post.id}`)}
            >
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#06B6D4]/10 text-[#06B6D4] border border-[#06B6D4]/20 mb-4">
                {post.category}
              </span>
              <h3 className="font-['Outfit'] text-base font-bold text-[#111827] mb-2 leading-snug">
                {post.title}
              </h3>
              <p className="text-sm text-[#6B7280] mb-4">{post.excerpt}</p>
              <div className="flex items-center justify-between text-xs text-[#6B7280] mb-3">
                <span>{post.read_time}</span>
                <span>{formatDate(post.created_at)}</span>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); if (post.id) navigate(`/blog/${post.id}`); }}
                className="inline-flex items-center text-sm font-medium text-[#06B6D4] hover:text-[#0596B5]"
                data-testid={`blog-read-more-${i}`}
              >
                Read More <ArrowRight className="ml-1 w-3.5 h-3.5" />
              </button>
            </div>
          ))}

          {/* Placeholder cards with Coming Soon */}
          {placeholders.map((post, i) => (
            <div
              key={`placeholder-${i}`}
              className="relative bg-white border border-[#E5E7EB] rounded-2xl p-6 overflow-hidden"
              data-testid={`blog-placeholder-${i}`}
            >
              <div className="blur-[1.5px]">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#06B6D4]/10 text-[#06B6D4] border border-[#06B6D4]/20 mb-4">
                  {post.category}
                </span>
                <h3 className="font-['Outfit'] text-base font-bold text-[#111827] mb-2 leading-snug">
                  {post.title}
                </h3>
                <p className="text-sm text-[#6B7280] mb-4">{post.excerpt}</p>
                <div className="flex items-center justify-between text-xs text-[#6B7280]">
                  <span>{post.readTime}</span>
                  <span>Coming Soon</span>
                </div>
              </div>
              <div className="absolute inset-0 bg-[#F9FAFB]/50 backdrop-blur-[1px] flex items-center justify-center rounded-2xl">
                <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#0F172A] text-white" data-testid="blog-coming-soon-badge">
                  Coming Soon
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Subscribe section */}
        {hasPlaceholders && (
          <div className="mt-10 text-center" data-testid="blog-subscribe-section">
            <p className="text-sm text-[#6B7280] mb-3">Get notified when new articles drop</p>
            {subscribed ? (
              <p className="text-sm font-medium text-[#06B6D4]" data-testid="subscribe-success">Subscribed! We'll keep you posted.</p>
            ) : (
              <form onSubmit={handleSubscribe} className="flex gap-2 max-w-sm mx-auto" data-testid="subscribe-form">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-white border-[#E5E7EB] focus:ring-[#06B6D4]/50 focus:border-[#06B6D4]"
                  data-testid="subscribe-email-input"
                  required
                />
                <button
                  type="submit"
                  className="px-5 py-2 rounded-lg bg-[#0F172A] text-white text-sm font-medium hover:bg-[#111827] transition-all shrink-0"
                  data-testid="subscribe-submit-btn"
                >
                  Subscribe
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
